import { CalendarRangePicker } from '@shared/components/CalendarRangePicker';
import { Box, FormControl } from '@withjoy/joykit';
import { useField } from 'formik';
import React, { useMemo } from 'react';
import * as Yup from 'yup';
import { SchemaDefaultValues } from './helpers';
import * as DateFNS from 'date-fns';
import { Calendar } from '@withjoy/joykit/icons';
import { pxToRem } from '@withjoy/joykit/theme';
import { useTranslation } from '@shared/core';
import { useMediaQuery } from '@withjoy/joykit/utils';

const schema = Yup.object({
  startDate: Yup.date(),
  endDate: Yup.date()
}).test('dates', 'Invalid dates', function (value) {
  if (!value) {
    return false;
  }
  return value.startDate && value.endDate;
});
// TODO: type this based on Yup.InferType<typeof schema> in a way that works with eslint
type ISchema = {
  startDate: Date | null;
  endDate: Date | null;
};

const initialValue: SchemaDefaultValues<typeof schema> = {
  startDate: undefined,
  endDate: undefined
};

const getDateRangeDisplay = (startDate: Date | null, endDate: Date | null) => {
  const fmtDate = (date: Date | null) => (date ? DateFNS.format(date, 'MMM d') : '');
  // Thur, Jan 1 - Sun, Jan 4
  return `${fmtDate(startDate)} - ${fmtDate(endDate)}`;
};

const getDateDisplay = (date: Date | null) => {
  const fmtDate = (date: Date | null) => (date ? DateFNS.format(date, 'MMM d') : '');
  // Thur, Jan 1
  return `${fmtDate(date)}`;
};

const Fields = ({ fieldsId, defaultEndDate, defaultStartDate }: { fieldsId: string; defaultStartDate?: Date; defaultEndDate?: Date }) => {
  // using DateRange as an uncontrolled component
  const [field, meta, helper] = useField<ISchema>(fieldsId);
  const handleChange = ([start, end]: [Date | undefined, Date | undefined]) => {
    helper.setValue({
      startDate: start || null,
      endDate: end || null
    });
  };
  const errorProps = useMemo(() => {
    return {
      isInvalid: meta.touched && !!meta.error,
      error: (meta.touched && meta.error) || undefined
    };
  }, [meta.touched, meta.error]);

  return (
    <FormControl {...errorProps}>
      <CalendarRangePicker initial={[field.value.startDate || defaultStartDate, field.value.endDate || defaultEndDate]} onChange={handleChange} closeOnSelectDate>
        <FakeInput>{getDateRangeDisplay(field.value.startDate, field.value.endDate)}</FakeInput>
      </CalendarRangePicker>
    </FormControl>
  );
};

const CardFields = ({ fieldsId, defaultEndDate, defaultStartDate }: { fieldsId: string; defaultStartDate?: Date; defaultEndDate?: Date }) => {
  const { t } = useTranslation('stays');
  const datesOccupantsTrans = t('datesOccupants');
  const isTabletScreen = useMediaQuery(theme => theme.mediaQueries.between({ breakpointAlias: 'sm2' }, { breakpointAlias: 'md' }));
  // using DateRange as an uncontrolled component
  const [field, meta, helper] = useField<ISchema>(fieldsId);
  const handleChange = ([start, end]: [Date | undefined, Date | undefined]) => {
    helper.setValue({
      startDate: start || null,
      endDate: end || null
    });
  };
  const errorProps = useMemo(() => {
    return {
      isInvalid: meta.touched && !!meta.error,
      error: (meta.touched && meta.error) || undefined
    };
  }, [meta.touched, meta.error]);

  return (
    <CalendarRangePicker initial={[field.value.startDate || defaultStartDate, field.value.endDate || defaultEndDate]} onChange={handleChange} closeOnSelectDate>
      <Box width="100%" display={'grid'} gridColumn={'1 / 3'} gridTemplateColumns={isTabletScreen ? '1fr' : '1fr 1fr'} cursor="pointer" borderColor="mono3">
        <Box paddingY={pxToRem(12)} paddingX={pxToRem(16)} display={'flex'} flexDirection={'column'} gap={pxToRem(4)}>
          <Box typographyVariant={'label3'} color={'mono14'}>
            {datesOccupantsTrans.checkIn()}
          </Box>
          <Box typographyVariant={'body2'} color={errorProps.isInvalid ? 'red6' : 'mono14'}>
            {getDateDisplay(field.value.startDate)}
          </Box>
        </Box>
        <Box
          {...(isTabletScreen && { borderTop: '1px solid' })}
          {...(!isTabletScreen && { borderLeft: '1px solid' })}
          paddingY={pxToRem(12)}
          paddingX={pxToRem(16)}
          display={'flex'}
          flexDirection={'column'}
          gap={pxToRem(4)}
          borderColor="mono3"
        >
          <Box typographyVariant={'label3'} color={'mono14'}>
            {datesOccupantsTrans.checkOut()}
          </Box>
          <Box typographyVariant={'body2'} color={errorProps.isInvalid ? 'red6' : 'mono14'}>
            {getDateDisplay(field.value.endDate)}
          </Box>
        </Box>
        {errorProps.isInvalid && errorProps.error && (
          <Box
            width="100%"
            borderTop={'1px solid'}
            borderColor="mono3"
            color={errorProps.isInvalid ? 'red6' : 'mono14'}
            paddingY={pxToRem(12)}
            paddingX={pxToRem(16)}
            display={'flex'}
            flexDirection={'column'}
            gap={pxToRem(4)}
            gridColumn={'1 / 3'}
          >
            {errorProps.error}
          </Box>
        )}
      </Box>
    </CalendarRangePicker>
  );
};

const FakeInput = React.forwardRef<HTMLDivElement, { children: React.ReactNode }>(({ children, ...restProps }, ref) => {
  return (
    <Box
      ref={ref}
      // spread required for FakeInput to work with PopoverV2
      {...restProps}
      display={'flex'}
      alignItems={'center'}
      cursor={'pointer'}
      borderRadius={'0.25rem'}
      paddingX={'1rem'}
      whiteSpace={'nowrap'}
      gap={3}
      typographyVariant={'label2'}
    >
      <Calendar />
      {children}
    </Box>
  );
});

FakeInput.displayName = 'FakeInput';

export { Fields, CardFields, schema, initialValue };
